import { ChangeEvent, FormEvent, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Flex,
  Form,
  FormContext,
  FormProvider,
  toast,
  useServerResponseTranslation,
  Text,
} from 'dash-ui';

import { useUpdatePasswordMutation } from 'gql/generated.graphql';

import useValidation from './ChangePassword.hooks';
import { FormFields, FormFieldsList } from './ChangePassword.constants';

const ChangePasswordForm = () => {
  const formRef = useRef<HTMLFormElement>(null);

  const { t } = useTranslation(['forms', 'common', 'pages']);

  const { formState, formErrors, stateHandlers, resetForm, resetFormErrors } =
    useContext(FormContext);

  const validate = useValidation();
  const translateErrorMessage = useServerResponseTranslation();

  const [updatePassword, { loading }] = useUpdatePasswordMutation();

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (
      !validate(FormFields.Password, formState.password) ||
      !validate(FormFields.NewPassword, formState.newPassword) ||
      !validate(FormFields.ConfirmPassword, formState.confirmPassword)
    ) {
      return null;
    }

    const input = {
      oldPassword: formState.password,
      newPassword: formState.newPassword,
    };

    return updatePassword({
      variables: input,
      onCompleted: () => {
        toast.success(t('pages:profilePage.toasts.updatedPassword'));
      },
      onError: (error) => {
        toast.error(translateErrorMessage(error));
      },
    });
  };

  const onInputChanged =
    (type: FormFields) => (e: ChangeEvent<HTMLInputElement>) => {
      stateHandlers[type](e.target.value);
      validate(type, e.target.value);
    };

  return (
    <Form ref={formRef} onSubmit={onSubmit} w={307}>
      <Form.Group mt={4}>
        <Form.Label>{t`labels.oldPassword`}</Form.Label>
        <Form.Input
          type="password"
          w="100%"
          placeholder={t`placeholders.password` || ''}
          onChange={onInputChanged(FormFields.Password)}
          $errorType={formErrors[FormFields.Password]?.type}
        />
        <Form.Feedback $errorType={formErrors[FormFields.Password]?.type}>
          {formErrors[FormFields.Password]?.message}
        </Form.Feedback>
      </Form.Group>
      <Form.Group mt={4}>
        <Form.Label>{t`labels.password`}</Form.Label>
        <Form.Input
          type="password"
          w="100%"
          placeholder={t`placeholders.newPassword` || ''}
          onChange={onInputChanged(FormFields.NewPassword)}
          $errorType={formErrors[FormFields.NewPassword]?.type}
        />
        <Form.Feedback $errorType={formErrors[FormFields.NewPassword]?.type}>
          {formErrors[FormFields.NewPassword]?.message}
        </Form.Feedback>
      </Form.Group>
      <Form.Group mt={4}>
        <Form.Label>{t`labels.confirmPassword`}</Form.Label>
        <Form.Input
          type="password"
          w="100%"
          placeholder={t`placeholders.confirmPassword` || ''}
          onChange={onInputChanged(FormFields.ConfirmPassword)}
          $errorType={formErrors[FormFields.ConfirmPassword]?.type}
        />
        <Form.Feedback
          $errorType={formErrors[FormFields.ConfirmPassword]?.type}
        >
          {formErrors[FormFields.ConfirmPassword]?.message}
        </Form.Feedback>
      </Form.Group>
      <Flex w={224} mt={4} justifyContent="space-between">
        <Button
          w={108}
          type="button"
          onClick={() => {
            if (formRef.current) {
              formRef.current.requestSubmit();
            }
          }}
          disabled={loading}
          isLoading={loading}
        >
          {t('common:labels.change')}
        </Button>
        <Button
          w={108}
          type="reset"
          variation="outline"
          onClick={() => {
            resetForm();
            resetFormErrors();
          }}
        >
          {t('common:labels.cancel')}
        </Button>
      </Flex>
    </Form>
  );
};

function ChangePassword() {
  const { t } = useTranslation(['pages']);

  return (
    <Flex flexDirection="column" mt={8}>
      <Text semibold>{t('pages:profilePage.settings.updatePassword')}</Text>
      <FormProvider fieldsList={FormFieldsList}>
        <ChangePasswordForm />
      </FormProvider>
    </Flex>
  );
}

export default ChangePassword;
